import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bot, Zap, Brain, MessageSquare, AlertTriangle, Settings,
  Play, Pause, Trash2, Plus, ChevronRight, Clock, Check,
  Activity, TrendingUp, Search, Send, History, Sparkles,
  RefreshCw, Eye, X, Shield, Target, Bell, Factory, Building2,
  Truck, ShoppingCart, Server, Heart, Sun, Briefcase, Star,
  Filter, Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { apiFetch } from '../services/api_service';


// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Workflow Card Component
const WorkflowCard = ({ workflow, onToggle, onDelete }) => {
  const [toggleLoading, setToggleLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isActive = workflow.status === 'active';

  const handleToggle = async () => {
    setToggleLoading(true);
    try {
      await onToggle(workflow.workflow_id);
    } finally {
      setToggleLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this workflow? This action cannot be undone.')) return;
    setDeleting(true);
    try {
      await onDelete(workflow.workflow_id);
    } catch {
      setDeleting(false);
    }
  };

  return (
    <Card className={`${!isActive ? 'opacity-60' : ''}`} data-testid={`workflow-${workflow.workflow_id}`}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isActive ? 'bg-green-100' : 'bg-gray-100'}`}>
              <Bot className={`h-5 w-5 ${isActive ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <p className="font-medium">{workflow.name}</p>
              <p className="text-xs text-gray-500">{workflow.description}</p>
            </div>
          </div>
          <Badge variant={isActive ? 'default' : 'secondary'} className={isActive ? 'bg-green-600' : ''}>
            {isActive ? 'Active' : 'Paused'}
          </Badge>
        </div>

        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
          <span className="flex items-center gap-1">
            <Zap className="h-3 w-3" />
            Trigger: {workflow.trigger?.replace(/_/g, ' ')}
          </span>
          <span className="flex items-center gap-1">
            <Activity className="h-3 w-3" />
            Executions: {workflow.execution_count || 0}
          </span>
        </div>

        {workflow.last_executed && (
          <p className="text-xs text-gray-400 mb-3">
            <Clock className="h-3 w-3 inline mr-1" />
            Last run: {new Date(workflow.last_executed).toLocaleString()}
          </p>
        )}

        <div className="flex items-center gap-2 pt-3 border-t">
          <Button 
            variant={isActive ? "outline" : "default"}
            size="sm" 
            onClick={handleToggle}
            disabled={toggleLoading || deleting}
            className="flex-1"
          >
            {isActive ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
            {toggleLoading ? 'Processing...' : isActive ? 'Pause' : 'Activate'}
          </Button>
          <Button 
            variant="destructive" 
            size="sm"
            onClick={handleDelete}
            disabled={deleting || toggleLoading}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid={`delete-workflow-${workflow.workflow_id}`}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Create Workflow Modal - Enhanced with Industries & Best Practices
const CreateWorkflowModal = ({ isOpen, onClose, templates, onCreate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('best_practices');
  const [activeIndustry, setActiveIndustry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  if (!isOpen) return null;

  const industries = templates?.industries || [];
  const bestPractices = templates?.best_practices || [];

  const handleCreate = () => {
    if (selectedTemplate) {
      onCreate(selectedTemplate);
      onClose();
      setSelectedTemplate(null);
      setActiveTab('best_practices');
      setActiveIndustry(null);
    } else {
      toast.error('Please select a workflow template');
    }
  };

  // Get templates based on active tab and filters
  const getDisplayTemplates = () => {
    let templatesArray = [];
    
    if (activeTab === 'best_practices') {
      templatesArray = bestPractices;
    } else if (activeTab === 'by_industry' && activeIndustry) {
      templatesArray = templates?.by_industry?.[activeIndustry] || [];
    } else if (activeTab === 'all') {
      templatesArray = Object.entries(templates?.templates || {}).map(([key, t]) => ({
        key,
        ...t,
        trigger: t.trigger?.value || t.trigger
      }));
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      templatesArray = templatesArray.filter(t => 
        t.name?.toLowerCase().includes(query) || 
        t.description?.toLowerCase().includes(query) ||
        t.industry?.toLowerCase().includes(query)
      );
    }
    
    return templatesArray;
  };

  const displayTemplates = getDisplayTemplates();

  const INDUSTRY_ICONS = {
    'Manufacturing': Factory,
    'Financial Services': Briefcase,
    'Energy & Utilities': Sun,
    'Retail & Consumer Goods': ShoppingCart,
    'Transportation & Logistics': Truck,
    'Real Estate & Construction': Building2,
    'Technology & Services': Server,
    'Healthcare & Pharmaceuticals': Heart
  };

  const INDUSTRY_COLORS = {
    'Manufacturing': 'bg-slate-100 text-slate-700 border-slate-300',
    'Financial Services': 'bg-blue-100 text-blue-700 border-blue-300',
    'Energy & Utilities': 'bg-amber-100 text-amber-700 border-amber-300',
    'Retail & Consumer Goods': 'bg-pink-100 text-pink-700 border-pink-300',
    'Transportation & Logistics': 'bg-orange-100 text-orange-700 border-orange-300',
    'Real Estate & Construction': 'bg-emerald-100 text-emerald-700 border-emerald-300',
    'Technology & Services': 'bg-violet-100 text-violet-700 border-violet-300',
    'Healthcare & Pharmaceuticals': 'bg-red-100 text-red-700 border-red-300'
  };

  const WORKFLOW_ICONS = {
    'check-circle': Check,
    'alert-triangle': AlertTriangle,
    'calendar': Clock,
    'file-text': Activity,
    'git-branch': TrendingUp,
    'alert-octagon': Shield,
    'check-square': Target,
    'link': Zap,
    'refresh-cw': RefreshCw,
    'clipboard-check': Eye,
    'share-2': Send,
    'zap': Zap,
    'factory': Factory,
    'trash-2': AlertTriangle,
    'pie-chart': Activity,
    'thermometer': Activity,
    'filter': Filter,
    'sun': Sun,
    'shopping-cart': ShoppingCart,
    'package': ShoppingCart,
    'truck': Truck,
    'battery-charging': Zap,
    'building': Building2,
    'hammer': Building2,
    'server': Server,
    'home': Building2,
    'activity': Activity
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <CardHeader className="border-b bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Bot className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-white text-xl">Create Workflow Automation</CardTitle>
                <CardDescription className="text-emerald-100">
                  Select from industry best practices or browse all templates
                </CardDescription>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        
        {/* Tab Navigation */}
        <div className="border-b bg-gray-50 p-3">
          <div className="flex items-center gap-4">
            <div className="flex gap-2">
              <button
                onClick={() => { setActiveTab('best_practices'); setActiveIndustry(null); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  activeTab === 'best_practices'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border'
                }`}
              >
                <Star className="h-4 w-4" />
                Best Practices
                <Badge className="ml-1 bg-white/20">{bestPractices.length}</Badge>
              </button>
              <button
                onClick={() => setActiveTab('by_industry')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  activeTab === 'by_industry'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border'
                }`}
              >
                <Layers className="h-4 w-4" />
                By Industry
              </button>
              <button
                onClick={() => { setActiveTab('all'); setActiveIndustry(null); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  activeTab === 'all'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border'
                }`}
              >
                <Filter className="h-4 w-4" />
                All Templates
              </button>
            </div>
            
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          
          {/* Industry Pills (when By Industry is selected) */}
          {activeTab === 'by_industry' && (
            <div className="flex flex-wrap gap-2 mt-3">
              {industries.map((industry) => {
                const IndustryIcon = INDUSTRY_ICONS[industry] || Building2;
                const isActive = activeIndustry === industry;
                return (
                  <button
                    key={industry}
                    onClick={() => setActiveIndustry(industry)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 border ${
                      isActive
                        ? INDUSTRY_COLORS[industry] || 'bg-emerald-100 text-emerald-700 border-emerald-300'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <IndustryIcon className="h-3.5 w-3.5" />
                    {industry}
                    <Badge variant="secondary" className="ml-1 text-[10px]">
                      {(templates?.by_industry?.[industry] || []).length}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Templates List */}
        <CardContent className="flex-1 overflow-y-auto p-4">
          {activeTab === 'by_industry' && !activeIndustry ? (
            <div className="grid grid-cols-2 gap-4">
              {industries.map((industry) => {
                const IndustryIcon = INDUSTRY_ICONS[industry] || Building2;
                const templateCount = (templates?.by_industry?.[industry] || []).length;
                return (
                  <button
                    key={industry}
                    onClick={() => setActiveIndustry(industry)}
                    className="p-6 border-2 border-dashed rounded-xl text-left hover:border-emerald-400 hover:bg-emerald-50 transition-all group"
                  >
                    <div className={`p-3 rounded-lg w-fit mb-3 ${INDUSTRY_COLORS[industry] || 'bg-gray-100'}`}>
                      <IndustryIcon className="h-6 w-6" />
                    </div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-emerald-700">{industry}</h3>
                    <p className="text-sm text-gray-500 mt-1">{templateCount} workflow templates</p>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {displayTemplates.length === 0 ? (
                <div className="text-center py-12">
                  <Bot className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No templates found</p>
                  <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filter</p>
                </div>
              ) : (
                displayTemplates.map((template) => {
                  const key = template.key;
                  const IconComponent = WORKFLOW_ICONS[template.icon] || Bot;
                  const isSelected = selectedTemplate === key;
                  const industryColor = INDUSTRY_COLORS[template.industry] || 'bg-gray-100 text-gray-700';
                  
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedTemplate(key)}
                      className={`w-full p-4 border-2 rounded-xl text-left transition-all ${
                        isSelected 
                          ? 'border-emerald-500 bg-emerald-50 shadow-lg ring-2 ring-emerald-200' 
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                      }`}
                      data-testid={`template-${key}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl ${industryColor}`}>
                          <IconComponent className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-gray-900">{template.name}</p>
                            {template.best_practice && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                <Star className="h-3 w-3 mr-1" />
                                Best Practice
                              </Badge>
                            )}
                            {isSelected && (
                              <div className="ml-auto flex items-center gap-1 text-emerald-600">
                                <Check className="h-5 w-5" />
                                <span className="text-sm font-medium">Selected</span>
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                          
                          {/* Use case */}
                          {template.use_case && (
                            <p className="text-xs text-gray-400 mt-2 italic">
                              💡 {template.use_case}
                            </p>
                          )}
                          
                          <div className="flex flex-wrap items-center gap-2 mt-3">
                            <Badge variant="outline" className="text-xs">
                              <Zap className="h-3 w-3 mr-1" />
                              {(template.trigger || '').replace(/_/g, ' ')}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {template.action_count || template.actions?.length || 0} actions
                            </Badge>
                            {template.industry && (
                              <Badge variant="secondary" className="text-xs">
                                {template.industry}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </CardContent>
        
        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {selectedTemplate ? (
                <span className="text-emerald-600 font-medium">
                  ✓ Template selected - Ready to create
                </span>
              ) : (
                'Select a template to create a workflow'
              )}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button 
                onClick={handleCreate} 
                disabled={!selectedTemplate}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Create Workflow
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// NL Query Component
const NLQueryInterface = () => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/autonomous-agents/nl-query/history?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setHistory(data.queries || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleQuery = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    setResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/autonomous-agents/nl-query`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query })
      });
      setResult(data);
      fetchHistory();
    } catch (error) {
      toast.error('Query failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleQuery();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-purple-600" />
          Natural Language Query
        </CardTitle>
        <CardDescription>Ask questions about your ESG data in plain English</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., What are my total emissions? How much evidence do I have?"
            className="flex-1"
          />
          <Button onClick={handleQuery} disabled={loading || !query.trim()}>
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>

        {result && (
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-start gap-3">
              <Brain className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <p className="text-gray-800">{result.answer?.answer || result.answer}</p>
                {result.answer?.confidence && (
                  <Badge variant="outline" className="mt-2">
                    {result.answer.confidence} confidence
                  </Badge>
                )}
                {result.answer?.suggestions?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {result.answer.suggestions.map((s, idx) => (
                      <button
                        key={idx}
                        onClick={() => setQuery(s)}
                        className="text-xs text-purple-600 hover:underline"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
              <History className="h-3 w-3" />
              Recent queries
            </p>
            <div className="space-y-1">
              {history.slice(0, 3).map((h, idx) => (
                <button
                  key={idx}
                  onClick={() => setQuery(h.query)}
                  className="w-full text-left text-sm text-gray-600 hover:bg-gray-50 p-2 rounded"
                >
                  "{h.query}"
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Component
const AutonomousAgents = () => {
  const [dashboard, setDashboard] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState({});
  const [anomalies, setAnomalies] = useState([]);
  const [corrections, setCorrections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState('workflows');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    await Promise.all([
      fetchDashboard(),
      fetchWorkflows(),
      fetchTemplates(),
      fetchAnomalies(),
      fetchCorrections()
    ]);
    setLoading(false);
  };

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/autonomous-agents/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDashboard(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/autonomous-agents/workflows`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWorkflows(data.workflows || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch(`/autonomous-agents/workflows/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response || response._error) {
        console.error('Templates API error:', response?.status);
        return;
      }
      setTemplates(response || {});
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchAnomalies = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/autonomous-agents/anomalies?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAnomalies(data.anomalies || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchCorrections = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/autonomous-agents/corrections/insights`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCorrections(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const createWorkflow = async (templateKey) => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/autonomous-agents/workflows`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ template_key: templateKey })
      });
      if (data.success) {
        toast.success('Workflow created');
        fetchWorkflows();
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to create workflow');
    }
  };

  const toggleWorkflow = async (workflowId) => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/autonomous-agents/workflows/${workflowId}/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (data.success) {
        toast.success(`Workflow ${data.status}`);
        fetchWorkflows();
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to toggle workflow');
    }
  };

  const deleteWorkflow = async (workflowId) => {
    if (!workflowId) {
      toast.error('Invalid workflow ID');
      return;
    }
    
    // Optimistically remove from UI immediately
    setWorkflows(prev => prev.filter(w => w.workflow_id !== workflowId));
    
    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch(`/autonomous-agents/workflows/${workflowId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response && !response._error) {
        toast.success('Workflow deleted successfully');
        fetchDashboard().catch(() => {});
      } else {
        toast.error(response?.message || 'Failed to delete workflow');
        // Revert: re-fetch if delete failed
        fetchWorkflows().catch(() => {});
      }
    } catch (error) {
      console.error('Delete workflow error:', error);
      toast.error('Network error - please try again');
      // Revert: re-fetch if delete failed
      fetchWorkflows().catch(() => {});
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="autonomous-agents">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 rounded-xl p-6 text-white mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Bot className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Autonomous Agents</h1>
              <p className="text-purple-100">AI-powered automation for your ESG workflows</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowCreate(true)}
            className="bg-white text-purple-600 hover:bg-purple-50"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Workflow
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <StatCard
          title="Active Workflows"
          value={dashboard?.active_workflows || 0}
          icon={Bot}
          color="bg-green-100 text-green-600"
        />
        <StatCard
          title="Executions (24h)"
          value={dashboard?.executions_24h || 0}
          icon={Zap}
          color="bg-blue-100 text-blue-600"
        />
        <StatCard
          title="Anomalies (7d)"
          value={dashboard?.anomalies_7d || 0}
          icon={AlertTriangle}
          color="bg-yellow-100 text-yellow-600"
        />
        <StatCard
          title="Corrections (7d)"
          value={dashboard?.corrections_7d || 0}
          icon={Target}
          color="bg-purple-100 text-purple-600"
        />
        <StatCard
          title="NL Queries (7d)"
          value={dashboard?.nl_queries_7d || 0}
          icon={MessageSquare}
          color="bg-pink-100 text-pink-600"
        />
      </div>

      {/* Quick Start Guide */}
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-5 mb-6">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Sparkles className="h-5 w-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 mb-2">How to Use Workflow Automation</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="bg-white p-3 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 text-purple-600 font-medium mb-1">
                  <span className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center text-xs">1</span>
                  Create a Workflow
                </div>
                <p className="text-slate-600">Click "New Workflow" and select from 30+ industry best practice templates covering Manufacturing, Finance, Energy, Retail, and more.</p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 text-purple-600 font-medium mb-1">
                  <span className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center text-xs">2</span>
                  Automatic Triggers
                </div>
                <p className="text-slate-600">Workflows run automatically when triggered - on data upload, threshold breach, compliance deadlines, or scheduled intervals.</p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 text-purple-600 font-medium mb-1">
                  <span className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center text-xs">3</span>
                  Monitor & Refine
                </div>
                <p className="text-slate-600">Track executions, review anomalies, and let the AI learn from your corrections to improve accuracy over time.</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-xs text-slate-500">
                <strong>Recommended workflows to start:</strong> Auto-Approve High Confidence Extractions • Compliance Deadline Reminder • Emissions Anomaly Alert
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="workflows" className="gap-2">
            <Bot className="h-4 w-4" />
            Workflows
            {workflows.length > 0 && <Badge variant="secondary">{workflows.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="nl-query" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            Ask AI
          </TabsTrigger>
          <TabsTrigger value="anomalies" className="gap-2">
            <AlertTriangle className="h-4 w-4" />
            Anomalies
          </TabsTrigger>
          <TabsTrigger value="learning" className="gap-2">
            <Brain className="h-4 w-4" />
            Learning
          </TabsTrigger>
        </TabsList>

        <TabsContent value="workflows">
          {workflows.length > 0 ? (
            <div className="grid grid-cols-2 gap-4">
              {workflows.map((workflow) => (
                <WorkflowCard
                  key={workflow.workflow_id}
                  workflow={workflow}
                  onToggle={toggleWorkflow}
                  onDelete={deleteWorkflow}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Bot className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-lg font-medium text-gray-700 mb-2">No Workflows Yet</p>
                <p className="text-gray-500 mb-4">Create your first autonomous workflow to automate ESG processes</p>
                <Button onClick={() => setShowCreate(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Workflow
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="nl-query">
          <NLQueryInterface />
        </TabsContent>

        <TabsContent value="anomalies">
          <Card>
            <CardHeader>
              <CardTitle>Anomaly Detection</CardTitle>
              <CardDescription>Unusual patterns detected in your ESG data</CardDescription>
            </CardHeader>
            <CardContent>
              {anomalies.length > 0 ? (
                <div className="space-y-3">
                  {anomalies.map((anomaly, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                      <AlertTriangle className={`h-5 w-5 mt-0.5 ${
                        anomaly.severity === 'critical' ? 'text-red-600' :
                        anomaly.severity === 'high' ? 'text-orange-600' : 'text-yellow-600'
                      }`} />
                      <div>
                        <p className="font-medium">{anomaly.message}</p>
                        <p className="text-sm text-gray-500">
                          {anomaly.field} • {anomaly.data_type} • Detected: {new Date(anomaly.detected_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant={anomaly.severity === 'critical' ? 'destructive' : 'outline'}>
                        {anomaly.severity}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Shield className="h-12 w-12 mx-auto text-green-400 mb-4" />
                  <p className="text-gray-500">No anomalies detected</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="learning">
          <Card>
            <CardHeader>
              <CardTitle>Learning from Corrections</CardTitle>
              <CardDescription>AI learns from your corrections to improve over time</CardDescription>
            </CardHeader>
            <CardContent>
              {corrections?.total_corrections > 0 ? (
                <div className="space-y-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="font-medium text-purple-700">
                      {corrections.total_corrections} total corrections recorded
                    </p>
                    {corrections.insights?.map((insight, idx) => (
                      <p key={idx} className="text-sm text-gray-600 mt-1">{insight}</p>
                    ))}
                  </div>
                  
                  {corrections.most_corrected_fields?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Most corrected fields</p>
                      <div className="space-y-2">
                        {corrections.most_corrected_fields.slice(0, 5).map((field, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm">
                              {field._id?.field} ({field._id?.document_type})
                            </span>
                            <Badge variant="secondary">{field.count} times</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Brain className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No corrections recorded yet</p>
                  <p className="text-sm text-gray-400">Corrections you make to AI extractions will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Modal */}
      <CreateWorkflowModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        templates={templates}
        onCreate={createWorkflow}
      />
    </div>
  );
};

export default AutonomousAgents;
